import { z } from 'zod'
import { Contract, ContractRegistry } from './contract.js'
import { Sha256 } from './document.js'
import { Block, RondeSleutel } from './id.js'
import { Moment, Person } from './moment.js'
import { Text } from './text.js'

/**
 * De contracten zoals de opslagdienst ze bewaart (ontwerp 9.5, U26, U27, D-37, D-43, B10, besluit van
 * 28 oktober 2026).
 *
 * Een contract wordt aangeleverd als XSD bij de opslagdienst. Elke aanlevering is een **versie**: een
 * volgnummer per blok, met de hash van het XSD, het XSD zelf en het contract dat de opslagdienst eruit
 * las. Een versie wordt nooit overschreven of gewist; terugdraaien is een nieuwe versie met de oude
 * inhoud. Naast de contracten staat de **registry-index**: de ronde, en per blok zijn plaats in de keten
 * en zijn weergavenaam. Ook die heeft versies. Samen zijn ze de registry van de ronde
 * (`ContractRegistry`), en de registry in versiebeheer is het startpakket waarmee een lege opslag
 * begint.
 *
 * **Strikt op de schrijfweg, ruim op de leesweg.** Een verzoek dat een sleutel draagt die de dienst
 * niet kent, wordt geweigerd. Een antwoord wordt gelezen zonder te weigeren wat er later bij komt: de
 * store-API mag er een veld bij krijgen (ontwerp 3.5), en een afnemer die dan omvalt, maakt van een
 * additieve wijziging een brekende. Zelfde keuze als `DefinitieChange` (D-74).
 */

/**
 * De naam waaronder het dossier-contract van het processysteem wordt bewaard (R-56).
 *
 * Het dossier-contract heeft geen blok en geen plaats in de keten, maar wel versies, net als een
 * blok. Het staat onder deze naam naast de blokken, zoals zijn XSD in `packages/model/contracten/`
 * `dossier.xsd` heet. Een blok met deze naam bestaat daarom niet.
 */
export const DOSSIER_CONTRACT = 'dossier'

/** Een contractnaam: een blok, of het dossier-contract. */
export const ContractName = Block
export type ContractName = z.infer<typeof ContractName>

/** Het volgnummer van een versie, per contract oplopend vanaf 1. */
export const ContractVersionNumber = z.number().int().positive()

/** Eén blok in de registry-index: zijn plaats in de keten en zijn weergavenaam. */
export const RegistryIndexBlock = z.strictObject({
  blok: Block.refine((block) => block !== DOSSIER_CONTRACT, `${DOSSIER_CONTRACT} is de naam van het dossier-contract en geen blok`),
  /** Plaats in de keten, oplopend vanaf 1 (D-38). */
  positie: z.number().int().positive(),
  /**
   * Hoe het blok heet waar een mens het leest, zoals boven het formulier in de contextkop (R-125,
   * R-157). Een publicatie bevriest de naam die gold toen ze werd gemaakt.
   */
  naam: Text,
})
export type RegistryIndexBlock = z.infer<typeof RegistryIndexBlock>

/** De registry-index: ronde en ketenvolgorde, zonder de contracten zelf. */
export const RegistryIndex = z
  .strictObject({
    ronde: z.string().min(1),
    bouwblokken: z.array(RegistryIndexBlock).min(1),
  })
  .superRefine((index, ctx) => {
    const blocks = new Set<string>()
    const positions = new Set<number>()
    index.bouwblokken.forEach((row, i) => {
      if (blocks.has(row.blok)) ctx.addIssue({ code: 'custom', path: ['bouwblokken', i, 'blok'], message: `blok ${row.blok} staat er twee keer` })
      blocks.add(row.blok)
      // Twee blokken op dezelfde plaats maken "eerder in de keten" onbeslisbaar (D-38).
      if (positions.has(row.positie)) ctx.addIssue({ code: 'custom', path: ['bouwblokken', i, 'positie'], message: `positie ${row.positie} staat er twee keer` })
      positions.add(row.positie)
    })
  })
export type RegistryIndex = z.infer<typeof RegistryIndex>

/** Eén versie van de registry-index, zoals de opslagdienst haar bewaart. */
export const RegistryIndexVersion = z.object({
  versie: z.number().int().positive(),
  ronde: z.string().min(1),
  bouwblokken: z.array(RegistryIndexBlock),
  vastgelegdOp: Moment,
  vastgelegdDoor: Person,
})
export type RegistryIndexVersion = z.infer<typeof RegistryIndexVersion>

/**
 * De index van een registry: wat er van de registry overblijft zonder de contracten.
 *
 * Gooit als een blok geen weergavenaam heeft, want de index in de opslag eist er een voor elk blok.
 */
export function registryIndexOf(registry: ContractRegistry): RegistryIndex {
  return RegistryIndex.parse({
    ronde: registry.ronde,
    bouwblokken: registry.bouwblokken.map((row) => {
      if (row.naam === undefined) throw new Error(`blok ${row.blok} heeft geen weergavenaam in de registry; de registry-index eist er een voor elk blok`)
      return { blok: row.blok, positie: row.positie, naam: row.naam }
    }),
  })
}

/**
 * Het verzoek om een bouwblok achteraan toe te voegen aan een bestaande ronde, met zijn eerste contract
 * (`POST /v1/rondes/:sleutel/bouwblokken`, besluit van 2 november 2026, eisenboek 12.52).
 *
 * **Geen positie in het verzoek.** De opslagdienst zet het blok achter het laatste, zodat er geen blok
 * verschuift en geen verwijzing van een bestaand formulier van richting verandert (D-41, D-40). Het XSD
 * is de eerste versie van het contract in die ronde; een blok dat er al staat krijgt langs deze weg geen
 * andere inhoud (D-43).
 */
export const BlockAddition = z.strictObject({
  blok: RegistryIndexBlock.shape.blok,
  naam: Text,
  xsd: z.string().min(1),
  persoon: Person,
})
export type BlockAddition = z.infer<typeof BlockAddition>

/** Het verzoek om een contract aan te leveren: het XSD en wie het aanlevert. */
export const ContractSubmission = z.strictObject({
  xsd: z.string().min(1),
  aangeleverdDoor: Person,
})
export type ContractSubmission = z.infer<typeof ContractSubmission>

/** Wat er van een versie in een lijst staat: alles behalve het XSD en het contract. */
export const ContractVersionEntry = z.object({
  /**
   * De ronde waarin deze versie is aangeleverd (D-75, migratie 0046). Het nummer loopt per naam over alle
   * rondes heen door, zodat naam en versie samen een versie blijven aanwijzen; de nieuwste versie geldt
   * per ronde. Optioneel op de leesweg: een opslagdienst van vóór die migratie geeft hem niet.
   */
  ronde: RondeSleutel.optional(),
  naam: ContractName,
  versie: ContractVersionNumber,
  /** De SHA-256 over de bytes van het XSD in UTF-8, met voorvoegsel, zoals bij een document (R-199). */
  hash: Sha256,
  aangeleverdOp: Moment,
  aangeleverdDoor: Person,
})
export type ContractVersionEntry = z.infer<typeof ContractVersionEntry>

/** Eén versie van een contract: het XSD zoals het kwam en het contract dat de opslagdienst eruit las. */
export const ContractVersion = ContractVersionEntry.extend({
  xsd: z.string().min(1),
  contract: Contract,
})
export type ContractVersion = z.infer<typeof ContractVersion>

/**
 * De registry zoals de opslagdienst hem nu geeft: de nieuwste index, met per blok de nieuwste versie
 * van zijn contract, en het dossier-contract.
 */
export const StoredRegistry = z.object({
  indexVersie: ContractVersionNumber,
  ronde: z.string().min(1),
  bouwblokken: z.array(
    z.object({
      blok: Block,
      positie: z.number().int().positive(),
      naam: Text,
      versie: ContractVersionNumber,
      hash: Sha256,
      contract: Contract,
    }),
  ),
  dossier: z.object({ versie: ContractVersionNumber, hash: Sha256, contract: Contract }).optional(),
})
export type StoredRegistry = z.infer<typeof StoredRegistry>

/** De registry uit de opslag in de vorm die de engine en de configurator lezen. */
export function registryFromStore(stored: StoredRegistry): ContractRegistry {
  return ContractRegistry.parse({
    ronde: stored.ronde,
    bouwblokken: stored.bouwblokken.map((row) => ({ blok: row.blok, positie: row.positie, naam: row.naam, contract: row.contract })),
    ...(stored.dossier === undefined ? {} : { dossier: stored.dossier.contract }),
  })
}

/**
 * Welke versie van elk contract een publicatie gebruikte, bevroren bij het publiceren (D-36, D-43).
 *
 * Een draaiende publicatie wordt niet aangepast (D-36), dus een document van buiten bij een instantie
 * van deze publicatie wordt getoetst aan de versie die hier staat en niet aan de nieuwste. `indexVersie`
 * zegt welke registry-index gold, en daarmee de ketenvolgorde en de weergavenaam van het blok.
 */
export const PublicatieContractVersies = z.strictObject({
  indexVersie: ContractVersionNumber,
  versies: z.record(ContractName, ContractVersionNumber),
})
export type PublicatieContractVersies = z.infer<typeof PublicatieContractVersies>
